
import { supabase } from "@/integrations/supabase/client";

/**
 * Busca a assinatura ativa do usuário de forma unificada.
 * Usa order + limit + maybeSingle para sempre pegar a mais recente,
 * evitando erro quando houver duplicatas (embora o índice parcial previna isso).
 */
export const getActiveSubscription = async (userId: string) => {
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .eq('user_id', userId)
    .eq('status', 'active')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Erro ao buscar assinatura ativa:', error);
    return null;
  }
  return data;
};

export const fetchSubscriptions = async () => {
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .eq('status', 'active')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const updateUserSubscription = async (userId: string, planType: 'free' | 'basic' | 'pro' | 'premium') => {
  const limits = getPlanLimits(planType);
  
  try {
    console.log(`Admin: Iniciando atualização de plano para usuário ${userId}: ${planType}`);
    
    // Primeiro: desativar TODAS as assinaturas ativas do usuário
    const { error: deactivateError } = await supabase
      .from('subscriptions')
      .update({ 
        status: 'inactive',
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId)
      .eq('status', 'active');

    if (deactivateError) {
      console.error('Admin: Erro ao desativar assinaturas existentes:', deactivateError);
      throw deactivateError;
    }

    // Segundo: criar nova assinatura ativa
    const startDate = new Date().toISOString();
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 30);
    const endDateISO = endDate.toISOString();

    const { data: newSubscription, error: insertError } = await supabase
      .from('subscriptions')
      .insert({
        user_id: userId,
        plan_type: planType,
        status: 'active',
        storage_limit: limits.storage,
        credits_limit: limits.credits,
        page_limit: limits.pageLimit,
        conversation_retention_days: limits.retentionDays,
        current_usage: 0,
        credits_used: 0,
        start_date: startDate,
        subscription_start_date: startDate,
        end_date: endDateISO,
        next_billing_date: endDateISO,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();
      
    if (insertError) {
      console.error('Admin: Erro ao criar nova assinatura:', insertError);
      throw insertError;
    }

    // Terceiro: atualizar ou criar registro de créditos para o mês atual
    const currentMonth = new Date().toISOString().slice(0, 7);
    
    const { data: existingCredits } = await supabase
      .from('user_credits')
      .select('*')
      .eq('user_id', userId)
      .eq('month_year', currentMonth)
      .maybeSingle();

    if (existingCredits) {
      const { error: updateCreditsError } = await supabase
        .from('user_credits')
        .update({
          limit_credits: limits.credits,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('month_year', currentMonth);

      if (updateCreditsError) throw updateCreditsError;
    } else {
      const { error: insertCreditsError } = await supabase
        .from('user_credits')
        .insert({
          user_id: userId,
          credits_input: 0,
          credits_output: 0,
          total_credits: 0,
          limit_credits: limits.credits,
          month_year: currentMonth,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (insertCreditsError) throw insertCreditsError;
    }

    return newSubscription;
    
  } catch (error) {
    console.error('Admin: Erro completo ao atualizar assinatura:', error);
    throw error;
  }
};

/** Limites por plano — fonte única de verdade */
export const getPlanLimits = (planType: string) => {
  switch (planType) {
    case 'premium':
      return { storage: 10737418240, credits: 2000000, pageLimit: 5000, retentionDays: 365 };
    case 'pro':
      return { storage: 5368709120, credits: 1000000, pageLimit: 2000, retentionDays: 365 };
    case 'basic':
      return { storage: 1073741824, credits: 500000, pageLimit: 300, retentionDays: 90 };
    case 'free':
    default:
      return { storage: 10485760, credits: 10000, pageLimit: 0, retentionDays: 30 };
  }
};

export const getPlanDetails = (planType: string) => {
  switch (planType) {
    case 'premium':
      return {
        name: 'Plano Premium',
        storageLimit: '2 GB',
        retentionDays: 365,
        price: 'R$124,50',
        originalPrice: 'R$249,00',
        description: 'Tudo do Pro + IA mais avançada e versão Beta + créditos',
        creditsLimit: '2M créditos',
        pageLimit: 5000,
        aiModel: 'GPT-4.1 + GPT-4o-mini'
      };
    case 'pro':
      return {
        name: 'Plano Pro',
        storageLimit: '1 GB',
        retentionDays: 365,
        price: 'R$74,50',
        originalPrice: 'R$149,00',
        description: 'Avançado',
        creditsLimit: '1M créditos',
        pageLimit: 2000,
        aiModel: 'GPT-4.1 + GPT-4o-mini'
      };
    case 'basic':
      return {
        name: 'Plano Básico',
        storageLimit: '100 MB',
        retentionDays: 90,
        price: 'R$44,50',
        originalPrice: 'R$89,00',
        annualPrice: 'R$39,90',
        annualTotal: 'R$478,80',
        description: 'Essencial',
        creditsLimit: '500K créditos',
        pageLimit: 300,
        aiModel: 'GPT-4.1 + GPT-4o-mini'
      };
    case 'free':
    default:
      return {
        name: 'Plano Gratuito',
        storageLimit: '10 MB',
        retentionDays: 30,
        price: 'R$0,00',
        description: 'Básico',
        creditsLimit: '10K créditos',
        pageLimit: 0,
        aiModel: 'GPT-4o-mini'
      };
  }
};

export const canSendMessage = async (userId: string): Promise<{canSend: boolean, nextAvailableTime?: Date}> => {
  const subscription = await getActiveSubscription(userId);
    
  // Se for um plano pago e ativo, pode enviar mensagens ilimitadas
  if (subscription && subscription.plan_type !== 'free') {
    return { canSend: true };
  }
  
  // Para usuários do plano gratuito, verificar se já enviou 3 mensagens nas últimas 24 horas
  const oneDayAgo = new Date();
  oneDayAgo.setHours(oneDayAgo.getHours() - 24);
  
  const { data: recentMessages } = await supabase
    .from('messages')
    .select('created_at')
    .eq('user_id', userId)
    .eq('role', 'user')
    .gte('created_at', oneDayAgo.toISOString())
    .order('created_at', { ascending: false });
    
  if (!recentMessages || recentMessages.length < 3) {
    return { canSend: true };
  }
  
  const lastMessageTime = new Date(recentMessages[0].created_at);
  const nextAvailableTime = new Date(lastMessageTime);
  nextAvailableTime.setHours(nextAvailableTime.getHours() + 24);
  
  const canSend = new Date() >= nextAvailableTime;
  
  return { 
    canSend, 
    nextAvailableTime: canSend ? undefined : nextAvailableTime 
  };
};

export const canUploadFile = async (userId: string): Promise<{canUpload: boolean, pageLimit: number, currentPlan: string}> => {
  const subscription = await getActiveSubscription(userId);
    
  if (!subscription) {
    return { canUpload: false, pageLimit: 0, currentPlan: 'free' };
  }
  
  const canUpload = subscription.plan_type !== 'free' && (subscription.page_limit || 0) > 0;
  
  return { 
    canUpload, 
    pageLimit: subscription.page_limit || 0, 
    currentPlan: subscription.plan_type 
  };
};

export const countPdfPages = async (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (event) => {
      try {
        const arrayBuffer = event.target?.result as ArrayBuffer;
        const uint8Array = new Uint8Array(arrayBuffer);
        
        const text = new TextDecoder().decode(uint8Array);
        const pageMatches = text.match(/\/Type\s*\/Page[^s]/g);
        const pageCount = pageMatches ? pageMatches.length : 1;
        
        resolve(Math.max(pageCount, 1));
      } catch (error) {
        console.error('Erro ao contar páginas do PDF:', error);
        const estimatedPages = Math.ceil(file.size / (50 * 1024));
        resolve(Math.max(estimatedPages, 1));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Erro ao ler o arquivo PDF'));
    };
    
    reader.readAsArrayBuffer(file);
  });
};

export const canProcessFile = async (userId: string, file: File): Promise<{
  canProcess: boolean;
  reason?: string;
  pageCount?: number;
  pageLimit?: number;
  currentPlan?: string;
}> => {
  try {
    const { canUpload, pageLimit, currentPlan } = await canUploadFile(userId);
    
    if (!canUpload) {
      return {
        canProcess: false,
        reason: currentPlan === 'free' 
          ? 'O plano gratuito não permite upload de arquivos. Faça upgrade para o plano Básico ou Pro.'
          : 'Plano não permite upload de arquivos.',
        currentPlan
      };
    }
    
    if (file.type === 'application/pdf') {
      const pageCount = await countPdfPages(file);
      
      if (pageCount > pageLimit) {
        return {
          canProcess: false,
          reason: `Este PDF tem ${pageCount} páginas, mas seu plano ${currentPlan === 'basic' ? 'Básico' : 'Pro'} permite até ${pageLimit} páginas. Faça upgrade para processar arquivos maiores.`,
          pageCount,
          pageLimit,
          currentPlan
        };
      }
      
      return {
        canProcess: true,
        pageCount,
        pageLimit,
        currentPlan
      };
    }
    
    return {
      canProcess: true,
      pageLimit,
      currentPlan
    };
    
  } catch (error) {
    console.error('Erro ao verificar se pode processar arquivo:', error);
    return {
      canProcess: false,
      reason: 'Erro ao verificar permissões do arquivo.'
    };
  }
};
